/**
 * Kleine Formatierungsleiste über der Auswahl beim „Text ändern“ — wie die
 * Mini-Leiste in Word: fett, kursiv, unterstrichen, Farben und Markierung aus
 * dem Brand-Design (wie im Assistenten), Link, Emoji, Formatierung entfernen.
 *
 * Die Leiste formatiert nicht selbst; sie meldet, was gewünscht ist, und die
 * Bühne führt es im Rahmen aus (lib/zusammenarbeit/formatieren.ts). Ein Klick
 * in die Leiste nimmt dem Rahmen weder Fokus noch Auswahl.
 */
import { useEffect, useRef, useState } from 'react';
import { Baseline, Bold, Check, Highlighter, Italic, Link as LinkIcon, RemoveFormatting, Smile, Underline, Unlink } from 'lucide-react';
import { TEXT_FARBEN, type TextFarbe } from '../../lib/mailBuilder/render';
import { EMOJIS, linkAdresse, type Farbe, type Format } from '../../lib/zusammenarbeit/formatieren';
import '../TextEditor/TextEditor.css';

export type LeistenMenue = 'farbe' | 'emoji' | 'link';

export interface LeistenStand {
  auswahl: boolean;
  fett: boolean;
  kursiv: boolean;
  unterstrichen: boolean;
  link: string | null;
  /** Mitte der Auswahl und ihre Ober-/Unterkante (px in der Hülle) */
  x: number;
  oben: number;
  unten: number;
  /** Über der Auswahl läge die Leiste unter der klebenden Werkzeugleiste → darunter zeigen */
  drunter: boolean;
}

export type LinkErgebnis = 'ok' | 'ohne_auswahl' | 'ungueltig' | 'fehler';

type Props = {
  stand: LeistenStand;
  /** Breite der Hülle (px) */
  weite: number;
  menue: LeistenMenue | null;
  onMenue: (m: LeistenMenue | null) => void;
  onFormat: (f: Format) => void;
  onFarbe: (f: Farbe) => void;
  onLink: (href: string) => LinkErgebnis;
  onLinkEntfernen: () => void;
  onEmoji: (e: string) => void;
  onFormatEntfernen: () => void;
};

/** Höhe der Leiste samt Abstand zur Auswahl (px). */
const ABSTAND = 50;
const HALBE_BREITE = 168;

const nichtFokussieren = (e: React.MouseEvent | React.PointerEvent) => {
  const ziel = e.target as HTMLElement;
  if (ziel.closest('input')) return;
  e.preventDefault();
};

export function FormatLeiste({ stand, weite, menue, onMenue, onFormat, onFarbe, onLink, onLinkEntfernen, onEmoji, onFormatEntfernen }: Props) {
  const [adresse, setAdresse] = useState(stand.link ?? '');
  const [fehler, setFehler] = useState<string | null>(null);
  const eingabeRef = useRef<HTMLInputElement>(null);

  // Link-Feld: mit der Adresse des Links unter der Auswahl beginnen
  useEffect(() => {
    if (menue !== 'link') return;
    setAdresse(stand.link ?? '');
    setFehler(null);
    window.setTimeout(() => eingabeRef.current?.focus(), 0);
    // nur beim Öffnen — nicht bei jeder Bewegung der Auswahl
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menue]);

  const halb = Math.min(HALBE_BREITE, weite / 2);
  const stil = {
    left: Math.min(Math.max(stand.x, halb), Math.max(halb, weite - halb)),
    top: stand.oben > ABSTAND + 6 && !stand.drunter ? stand.oben - ABSTAND : stand.unten + 10,
  };
  const ohneAuswahl = 'Markiere zuerst den Text.';
  const umschalten = (m: LeistenMenue) => onMenue(menue === m ? null : m);

  const linkSetzen = (e: React.FormEvent) => {
    e.preventDefault();
    const href = linkAdresse(adresse);
    if (!href) {
      setFehler(/^http:\/\//i.test(adresse.trim())
        ? 'Bitte eine sichere Adresse mit https:// angeben.'
        : 'Bitte eine Webadresse (https://…) oder eine Mailadresse eingeben.');
      return;
    }
    const r = onLink(href);
    if (r === 'ok') { onMenue(null); return; }
    setFehler(r === 'ohne_auswahl' ? 'Markiere zuerst den Text, der zum Link werden soll.'
      : r === 'ungueltig' ? 'Diese Adresse geht nicht als Link.'
        : 'Das hat nicht geklappt — markiere den Text bitte noch einmal.');
  };

  return (
    <div
      className="za-format"
      role="toolbar"
      aria-label="Text formatieren"
      style={stil}
      onMouseDown={nichtFokussieren}
      onPointerDown={nichtFokussieren}
      onKeyDown={(e) => { if (e.key === 'Escape' && menue) { e.stopPropagation(); onMenue(null); } }}
    >
      <div className="za-format-knoepfe">
        <button type="button" className={`te-knopf${stand.fett ? ' aktiv' : ''}`} aria-pressed={stand.fett} title="Fett (Strg+B)" aria-label="Fett" onClick={() => onFormat('fett')}>
          <Bold size={15} aria-hidden="true" />
        </button>
        <button type="button" className={`te-knopf${stand.kursiv ? ' aktiv' : ''}`} aria-pressed={stand.kursiv} title="Kursiv (Strg+I)" aria-label="Kursiv" onClick={() => onFormat('kursiv')}>
          <Italic size={15} aria-hidden="true" />
        </button>
        <button type="button" className={`te-knopf${stand.unterstrichen ? ' aktiv' : ''}`} aria-pressed={stand.unterstrichen} title="Unterstrichen (Strg+U) — in Mails sparsam, es sieht schnell wie ein Link aus" aria-label="Unterstrichen" onClick={() => onFormat('unterstrichen')}>
          <Underline size={15} aria-hidden="true" />
        </button>
        <span className="te-trenner" aria-hidden="true" />
        <button
          type="button"
          className={`te-knopf${menue === 'farbe' ? ' aktiv' : ''}`}
          aria-haspopup="menu"
          aria-expanded={menue === 'farbe'}
          title={stand.auswahl ? 'Farbe oder Markierung' : `Farbe — ${ohneAuswahl}`}
          aria-label="Farbe"
          disabled={!stand.auswahl}
          onClick={() => umschalten('farbe')}
        >
          <Baseline size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`te-knopf${menue === 'link' || stand.link ? ' aktiv' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={menue === 'link'}
          title={stand.link ? `Link ändern (Strg+K): ${stand.link}` : stand.auswahl ? 'Link (Strg+K)' : `Link — ${ohneAuswahl}`}
          aria-label={stand.link ? 'Link ändern' : 'Link'}
          disabled={!stand.auswahl && !stand.link}
          onClick={() => umschalten('link')}
        >
          <LinkIcon size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`te-knopf${menue === 'emoji' ? ' aktiv' : ''}`}
          aria-haspopup="menu"
          aria-expanded={menue === 'emoji'}
          title="Emoji einfügen"
          aria-label="Emoji einfügen"
          onClick={() => umschalten('emoji')}
        >
          <Smile size={15} aria-hidden="true" />
        </button>
        <span className="te-trenner" aria-hidden="true" />
        <button type="button" className="te-knopf" title={stand.auswahl ? 'Formatierung entfernen' : `Formatierung entfernen — ${ohneAuswahl}`} aria-label="Formatierung entfernen" disabled={!stand.auswahl} onClick={onFormatEntfernen}>
          <RemoveFormatting size={15} aria-hidden="true" />
        </button>
      </div>

      {menue === 'farbe' && (
        <div className="te-menue za-format-menue" role="menu" aria-label="Farbe">
          {(Object.keys(TEXT_FARBEN) as TextFarbe[]).map((f) => (
            <button key={f} type="button" role="menuitem" onClick={() => onFarbe(f)}>
              <span className="te-farbe" style={{ background: TEXT_FARBEN[f].hex }} aria-hidden="true" /> {TEXT_FARBEN[f].label}
            </button>
          ))}
          <button type="button" role="menuitem" onClick={() => onFarbe('markiert')}>
            <Highlighter size={14} aria-hidden="true" /> Markieren wie mit dem Textmarker
          </button>
        </div>
      )}

      {menue === 'emoji' && (
        <div className="te-menue za-format-menue za-format-emojis" role="menu" aria-label="Emoji einfügen">
          {EMOJIS.map((e) => (
            <button key={e} type="button" role="menuitem" aria-label={`${e} einfügen`} onClick={() => onEmoji(e)}>{e}</button>
          ))}
        </div>
      )}

      {menue === 'link' && (
        <form className="te-menue za-format-menue za-format-link" role="dialog" aria-label="Link" onSubmit={linkSetzen}>
          <label className="za-format-link-label" htmlFor="za-format-link">Adresse des Links</label>
          <div className="za-format-link-zeile">
            <input
              ref={eingabeRef}
              id="za-format-link"
              className="form-input"
              type="text"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder="https://… oder name@randori-pro.de"
              value={adresse}
              onChange={(e) => { setAdresse(e.target.value); setFehler(null); }}
              aria-invalid={fehler ? true : undefined}
              aria-describedby={fehler ? 'za-format-link-fehler' : undefined}
            />
            <button type="submit" className="za-format-link-ok" aria-label="Link übernehmen" title="Link übernehmen">
              <Check size={14} aria-hidden="true" />
            </button>
          </div>
          {fehler && <p id="za-format-link-fehler" className="za-format-link-fehler" role="alert">{fehler}</p>}
          {stand.link && (
            <button type="button" className="za-format-link-weg" onClick={onLinkEntfernen}>
              <Unlink size={14} aria-hidden="true" /> Link entfernen
            </button>
          )}
        </form>
      )}
    </div>
  );
}
